import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Badge,
  Flex,
  Grid,
  GridItem,
  Select,
  Input,
  InputGroup,
  InputLeftElement,
  Icon,
  Button,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Switch,
  Textarea,
  useDisclosure,
  useToast,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  IconButton,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import {
  MdSearch,
  MdAdd,
  MdDelete,
  MdEdit,
  MdEventBusy,
  MdCheckCircle,
  MdWarning,
} from 'react-icons/md';
import {
  getAllAbsences,
  createAbsence,
  updateAbsence,
  deleteAbsence,
} from 'services/absence.service';
import httpClient from '../../../utils/httpClient';

const TYPE_MAP = {
  UNJUSTIFIED: { label: 'Injustificada', color: 'red' },
  JUSTIFIED: { label: 'Justificada', color: 'green' },
  MEDICAL: { label: 'Médica', color: 'blue' },
  PERSONAL: { label: 'Personal', color: 'purple' },
  OTHER: { label: 'Otra', color: 'gray' },
};

export default function AbsenceManagementView() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const cardBg = useColorModeValue('white', 'navy.800');
  const toast = useToast();

  const [absences, setAbsences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [yearFilter, setYearFilter] = useState(new Date().getFullYear());
  const [monthFilter, setMonthFilter] = useState('');
  const [employees, setEmployees] = useState([]);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({
    employeeId: '',
    absenceDate: '',
    absenceType: 'UNJUSTIFIED',
    reason: '',
    justified: false,
    deductible: true,
    halfDay: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [yearFilter, monthFilter]);

  useEffect(() => {
    loadEmployees();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (yearFilter) filters.year = yearFilter;
      if (monthFilter) filters.month = monthFilter;
      const res = await getAllAbsences(filters);
      setAbsences(res?.payload?.data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await httpClient.get('/employee');
      const emps = res?.payload || res?.data?.payload || [];
      setEmployees(Array.isArray(emps) ? emps : []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleOpenCreate = () => {
    setEditMode(false);
    setEditId(null);
    setForm({
      employeeId: '',
      absenceDate: '',
      absenceType: 'UNJUSTIFIED',
      reason: '',
      justified: false,
      deductible: true,
      halfDay: false,
    });
    onOpen();
  };

  const handleOpenEdit = (absence) => {
    setEditMode(true);
    setEditId(absence.id);
    setForm({
      employeeId: absence.employee_id,
      absenceDate: absence.absence_date?.split('T')[0],
      absenceType: absence.absence_type,
      reason: absence.reason || '',
      justified: !!absence.justified,
      deductible: !!absence.deductible,
      halfDay: !!absence.half_day,
    });
    onOpen();
  };

  const handleSave = async () => {
    if (!editMode && (!form.employeeId || !form.absenceDate)) {
      toast({ title: 'Completá empleado y fecha', status: 'warning', duration: 3000 });
      return;
    }

    try {
      setSaving(true);
      if (editMode) {
        await updateAbsence(editId, {
          absenceType: form.absenceType,
          reason: form.reason,
          justified: form.justified,
          deductible: form.deductible,
          halfDay: form.halfDay,
          updatedBy: 1,
        });
        toast({ title: 'Ausencia actualizada', status: 'success', duration: 3000 });
      } else {
        await createAbsence({
          employeeId: Number(form.employeeId),
          absenceDate: form.absenceDate,
          absenceType: form.absenceType,
          reason: form.reason,
          justified: form.justified,
          deductible: form.deductible,
          halfDay: form.halfDay,
          createdBy: 1,
        });
        toast({ title: 'Ausencia registrada', status: 'success', duration: 3000 });
      }
      onClose();
      loadData();
    } catch (error) {
      const msg = error?.response?.data?.message || 'Error al guardar';
      toast({ title: msg, status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta ausencia?')) return;
    try {
      await deleteAbsence(id);
      toast({ title: 'Ausencia eliminada', status: 'success', duration: 3000 });
      loadData();
    } catch (error) {
      toast({ title: 'Error', status: 'error', duration: 3000 });
    }
  };

  const filtered = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return absences.filter((a) =>
      a.employee_name?.toLowerCase().includes(term) ||
      a.employee_document?.toLowerCase().includes(term) ||
      a.reason?.toLowerCase().includes(term)
    );
  }, [absences, searchTerm]);

  const stats = useMemo(() => {
    const total = absences.length;
    const deductible = absences.filter((a) => a.deductible).length;
    const justified = absences.filter((a) => a.justified).length;
    return { total, deductible, justified };
  }, [absences]);

  const months = [
    { value: '', label: 'Todos' },
    { value: '1', label: 'Enero' }, { value: '2', label: 'Febrero' },
    { value: '3', label: 'Marzo' }, { value: '4', label: 'Abril' },
    { value: '5', label: 'Mayo' }, { value: '6', label: 'Junio' },
    { value: '7', label: 'Julio' }, { value: '8', label: 'Agosto' },
    { value: '9', label: 'Septiembre' }, { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' }, { value: '12', label: 'Diciembre' },
  ];

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4} mb={6}>
        <GridItem>
          <Card bg={cardBg} p={4}>
            <Stat>
              <StatLabel color="gray.500">Total ausencias</StatLabel>
              <StatNumber color="red.500">{stats.total}</StatNumber>
              <StatHelpText>En el período</StatHelpText>
            </Stat>
          </Card>
        </GridItem>
        <GridItem>
          <Card bg={cardBg} p={4}>
            <Stat>
              <StatLabel color="gray.500">Descontables</StatLabel>
              <StatNumber color="orange.500">{stats.deductible}</StatNumber>
              <StatHelpText>Se descuentan del sueldo</StatHelpText>
            </Stat>
          </Card>
        </GridItem>
        <GridItem>
          <Card bg={cardBg} p={4}>
            <Stat>
              <StatLabel color="gray.500">Justificadas</StatLabel>
              <StatNumber color="green.500">{stats.justified}</StatNumber>
              <StatHelpText>Con respaldo</StatHelpText>
            </Stat>
          </Card>
        </GridItem>
      </Grid>

      <Card p={4}>
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <Flex gap={3} align="center" flexWrap="wrap">
            <InputGroup w="280px">
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar empleado o motivo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
            <Select w="100px" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)}>
              {[2024, 2025, 2026, 2027].map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </Select>
            <Select w="150px" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)}>
              {months.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </Select>
          </Flex>
          <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={handleOpenCreate}>
            Registrar ausencia
          </Button>
        </Flex>

        {filtered.length === 0 ? (
          <Flex justify="center" py={10} direction="column" align="center">
            <Icon as={MdCheckCircle} w={12} h={12} color="green.300" mb={3} />
            <Text color="gray.500">No hay ausencias registradas en este período</Text>
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>Empleado</Th>
                  <Th borderColor={borderColor}>Fecha</Th>
                  <Th borderColor={borderColor}>Tipo</Th>
                  <Th borderColor={borderColor}>Motivo</Th>
                  <Th borderColor={borderColor}>Descontable</Th>
                  <Th borderColor={borderColor}>Media jornada</Th>
                  <Th borderColor={borderColor}>Acciones</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((a) => {
                  const typeInfo = TYPE_MAP[a.absence_type] || { label: a.absence_type, color: 'gray' };
                  return (
                    <Tr key={a.id} _hover={{ bg: rowHover }}>
                      <Td borderColor={borderColor}>
                        <Text fontWeight="600" fontSize="sm">{a.employee_name}</Text>
                        <Text fontSize="xs" color="gray.500">{a.employee_document}</Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {new Date(a.absence_date).toLocaleDateString('es-UY')}
                      </Td>
                      <Td borderColor={borderColor}>
                        <Badge colorScheme={typeInfo.color}>{typeInfo.label}</Badge>
                      </Td>
                      <Td borderColor={borderColor}>
                        <Text fontSize="sm" noOfLines={1}>{a.reason || '-'}</Text>
                      </Td>
                      <Td borderColor={borderColor}>
                        {a.deductible ? (
                          <Badge colorScheme="red">Sí</Badge>
                        ) : (
                          <Badge colorScheme="green">No</Badge>
                        )}
                      </Td>
                      <Td borderColor={borderColor}>
                        {a.half_day ? <Badge colorScheme="purple">½</Badge> : '-'}
                      </Td>
                      <Td borderColor={borderColor}>
                        <HStack spacing={1}>
                          <IconButton
                            size="xs"
                            icon={<MdEdit />}
                            variant="ghost"
                            onClick={() => handleOpenEdit(a)}
                            aria-label="Editar"
                          />
                          <IconButton
                            size="xs"
                            icon={<MdDelete />}
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => handleDelete(a.id)}
                            aria-label="Eliminar"
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </Card>

      {/* Modal crear/editar */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editMode ? 'Editar ausencia' : 'Registrar ausencia'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {!editMode && (
              <FormControl mb={4} isRequired>
                <FormLabel>Empleado</FormLabel>
                <Select
                  placeholder="Seleccionar empleado"
                  value={form.employeeId}
                  onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
                >
                  {employees.map((emp) => {
                    const name = emp.person
                      ? `${emp.person.name} ${emp.person.lastName || emp.person.last_name || ''}`
                      : `Empleado #${emp.id}`;
                    return (
                      <option key={emp.id} value={emp.id}>{name}</option>
                    );
                  })}
                </Select>
              </FormControl>
            )}

            {!editMode && (
              <FormControl mb={4} isRequired>
                <FormLabel>Fecha</FormLabel>
                <Input
                  type="date"
                  value={form.absenceDate}
                  onChange={(e) => setForm({ ...form, absenceDate: e.target.value })}
                />
              </FormControl>
            )}

            <FormControl mb={4}>
              <FormLabel>Tipo de ausencia</FormLabel>
              <Select
                value={form.absenceType}
                onChange={(e) => setForm({ ...form, absenceType: e.target.value })}
              >
                <option value="UNJUSTIFIED">Injustificada</option>
                <option value="JUSTIFIED">Justificada</option>
                <option value="MEDICAL">Médica (certificado)</option>
                <option value="PERSONAL">Personal</option>
                <option value="OTHER">Otra</option>
              </Select>
            </FormControl>

            <FormControl mb={4}>
              <FormLabel>Motivo</FormLabel>
              <Textarea
                placeholder="Describir motivo de la ausencia..."
                value={form.reason}
                onChange={(e) => setForm({ ...form, reason: e.target.value })}
                rows={2}
              />
            </FormControl>

            <Grid templateColumns="1fr 1fr 1fr" gap={4}>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontSize="sm">Justificada</FormLabel>
                <Switch
                  isChecked={form.justified}
                  onChange={(e) => setForm({ ...form, justified: e.target.checked })}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontSize="sm">Descontable</FormLabel>
                <Switch
                  isChecked={form.deductible}
                  onChange={(e) => setForm({ ...form, deductible: e.target.checked })}
                />
              </FormControl>
              <FormControl display="flex" alignItems="center">
                <FormLabel mb="0" fontSize="sm">Media jornada</FormLabel>
                <Switch
                  isChecked={form.halfDay}
                  onChange={(e) => setForm({ ...form, halfDay: e.target.checked })}
                />
              </FormControl>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={onClose}>Cancelar</Button>
            <Button colorScheme="brand" onClick={handleSave}>
              {editMode ? 'Actualizar' : 'Registrar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
